/**
 * @file PersonalPortfolio.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useState } from "react";
import AccountStatements from "../../modules/clients/AccountStatements";
import Widget from "../Widget";
import DgModal from "../../../../components/DgModal";
import Loader from "../Loader";
import FilterSelect from "../../../../components/dashboard/FilterSelect";
import DgLineChart from "../../../../components/dashboard/DgLineChart";
import { toMoney } from "../../../../utils/moneyUtils";
import WarningIcon from "@mui/icons-material/Warning";
import { formatAmount } from "../../../../utils/formatAmount";
import { getInitialEquityFromHistorical } from "../../../../utils/utils";

function PersonalPortfolio({
  width,
  data,
  loading,
  account,
  onFilter = () => {},
}) {
  const { historical = [] } = data;

  const userData = {
    totalCash: data.balance?.body?.balances?.total_cash || 0,
    strategy: data.balance?.body?.balances?.account_type || "bull",
    currentValue: data.balance?.body?.balances?.market_value || 0,
  };

  // const [equityAmount, setEquityAmount] = useState(0);
  // const [equityPercentage, setEquityPercentage] = useState(0);
  const [statementsModal, setStatementsModal] = useState(false);
  const [equityTimeFilter, setEquityTimeFilter] = useState(null);

  // useEffect(() => {
  //   if (Object.keys(data).length > 0) {

  //     setEquityAmount(formattedEquity);

  //     if (historical[0]?.totalEquity <= 0) {
  //       setEquityPercentage("-");
  //     } else {
  //       setEquityPercentage(
  //         (
  //           ((historical[historical.length - 1]?.totalEquity -
  //             historical[0]?.totalEquity) /
  //             historical[0]?.totalEquity) *
  //           100
  //         ).toFixed(2)
  //       );
  //     }
  //   }
  // }, [data]);

  const initialEquity = getInitialEquityFromHistorical(historical);
  const lastEquityAmount = historical[historical.length - 1]?.totalEquity || 0;
  const equityAmount = parseFloat(lastEquityAmount.toFixed(2)).toLocaleString();
  const equityDifference =
    historical[historical.length - 1]?.totalEquity - initialEquity;
  const equityPercentage = formatAmount(
    ((historical[historical.length - 1]?.totalEquity - initialEquity) /
      initialEquity) *
      100,
    2
  );

  const handleStatementsModal = (isOpen) => {
    setStatementsModal(isOpen);
  };

  const handleEquityTimeFilter = (filter) => {
    setEquityTimeFilter(filter);
  };
  
  const handleResetFilters = () => {
    setEquityTimeFilter(null);
  };

  return (
    <Widget name={"personal-portfolio"} width={width}>
      <div className="heading">
        <h1 className="title">Personal Portfolio</h1>
        <>
          <p style={{ color: "#95b2c5" }}>
            <WarningIcon />
            {"  "}We value your privacy and are committed to protecting your
            personal information. We do not sell, trade, or share your
            information with third parties for marketing purposes.
          </p>
        </>
        <p className="subtitle">
          Account: {account.id || "-"} <br />
          Cash available: <span>{toMoney(userData.totalCash)}</span>
        </p>
      </div>

      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <FilterSelect
            onFilter={onFilter}
            sendFilterToParent={handleEquityTimeFilter}
          />
          {equityTimeFilter && (
            <button
              className="reset-filters-btn"
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>
          )}
        </div>
        {!loading ? (
          <DgLineChart
            data={historical}
            value={"totalEquity"}
            title={"Total equity"}
            height={340}
          />
        ) : (
          <Loader></Loader>
        )}

        <div className="value-info">
          <div
            className="btn gradient"
            style={{
              color:
                equityPercentage >= 0 || equityPercentage === "-"
                  ? "#000"
                  : "#ef4444",
            }}
          >
            {equityPercentage}%
          </div>
          <p className="subtitle">Current value</p>
          <h2 className="current-value">${equityAmount}</h2>
          <h5
            className={`amount-performance ${
              equityDifference < 0 ? "negative" : ""
            }`}
          >
            {equityDifference > 0 && "+"}
            {toMoney(equityDifference)}
          </h5>
        </div>
      </>

      <DgModal
        modalState={statementsModal}
        handleModalState={handleStatementsModal}
      >
        <AccountStatements />
      </DgModal>
    </Widget>
  );
}

export default PersonalPortfolio;
